import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';
import { PageDataService } from '../../core/page-data.service';
import { SiteConfigService } from '../../core/site-config.service';
import { DynamicFormField } from '../forms/dynamic-form-fields.component';

export type ServiceRequestMode = 'instant' | 'quote';

export interface ServiceCatalogItem {
  id: number;
  versionID: number;
  slug: string;
  title: string;
  description: string;
  imageUrl: string | null;
  imageSizes: Record<string, unknown> | unknown[];
  flowType: 'none' | 'form' | 'stepper';
  providerCount: number;
  minPrice: number | null;
  currency: string;
  durationMinutes: number;
  instantAvailable: boolean;
  quoteAvailable: boolean;
}

export interface ServiceIntakeStep {
  id: number | null;
  stepKey: string;
  title: string;
  formID: number;
  displayOrder: number;
  isRequired: boolean;
  fields: DynamicFormField[];
}

export interface ServiceIntakeFlow {
  listingID: number;
  flowID: number | null;
  flowType: 'none' | 'form' | 'stepper';
  stepProcessReminderID: number | null;
  steps: ServiceIntakeStep[];
}

export interface ServiceRequestMatch {
  offerID: number;
  sellerID: number;
  sellerName: string;
  price: number | null;
  currency: string;
  durationMinutes: number;
  description: string;
}

export interface ServiceRequestResult {
  requestID: number;
  requestToken: string;
  submissionID: number | null;
  requestMode: ServiceRequestMode;
  status: string;
  message: string;
  matches: ServiceRequestMatch[];
}

export interface ServiceRequestChoice {
  quoteID?: number;
  offerID: number;
  sellerID: number;
  sellerName: string;
  amount: number;
  currency: string;
  durationMinutes: number;
  message?: string;
  status?: string;
}

export interface ServiceRequestStatus {
  requestID: number;
  requestMode: ServiceRequestMode;
  status: string;
  serviceTitle: string;
  quotes: ServiceRequestChoice[];
  prices: ServiceRequestChoice[];
}

@Injectable({ providedIn: 'root' })
export class ServiceMarketplaceService {
  private readonly api = inject(ApiClientService);
  private readonly pageData = inject(PageDataService);
  private readonly site = inject(SiteConfigService);

  async catalog(filters: { search?: string; slug?: string; listingID?: number; limit?: number; offset?: number } = {}): Promise<ServiceCatalogItem[]> {
    await this.site.ready();
    const seedKey = filters.slug || filters.listingID ? 'listingData' : 'serviceCatalog';
    if (!filters.search && !filters.offset) {
      const seeded = this.pageData.get<unknown>(seedKey);
      if (seeded !== null) return this.catalogItems(this.payload(seeded, 'Hizmet kataloğu alınamadı.'));
    }
    const response = await firstValueFrom(this.api.post<unknown>('serviceMarketplacePublicCatalog', filters));
    const payload = this.payload(response, 'Hizmet kataloğu alınamadı.');
    return this.catalogItems(payload);
  }

  async detail(slug: string): Promise<ServiceCatalogItem | null> {
    const items = await this.catalog({ slug, limit: 1 });
    return items[0] ?? null;
  }

  async intakeFlow(listingID: number): Promise<ServiceIntakeFlow> {
    const seeded = this.pageData.get<unknown>('serviceIntake');
    if (seeded !== null) {
      const payload = this.payload(seeded, 'Hizmet bilgi formu alınamadı.');
      if (Number(payload['listingID'] ?? 0) === listingID) return this.normalizeIntake(payload, listingID);
    }
    const response = await firstValueFrom(this.api.post<unknown>('serviceMarketplacePublicIntakeFlow', { listingID }));
    const payload = this.payload(response, 'Hizmet bilgi formu alınamadı.');
    return this.normalizeIntake(payload, listingID);
  }

  private normalizeIntake(payload: Record<string, unknown>, listingID: number): ServiceIntakeFlow {
    return {
      listingID: Number(payload['listingID'] ?? listingID),
      flowID: Number(payload['flowID'] ?? 0) || null,
      flowType: this.flowType(payload['flowType']),
      stepProcessReminderID: Number(payload['stepProcessReminderID'] ?? 0) || null,
      steps: (Array.isArray(payload['steps']) ? payload['steps'] : []).map((raw) => {
        const step = raw as Record<string, unknown>;
        return {
          id: Number(step['id'] ?? 0) || null,
          stepKey: String(step['stepKey'] ?? `step-${step['formID'] ?? ''}`),
          title: String(step['title'] ?? 'Hizmet bilgileri'),
          formID: Number(step['formID'] ?? 0),
          displayOrder: Number(step['displayOrder'] ?? 0),
          isRequired: this.boolean(step['isRequired']),
          fields: Array.isArray(step['fields']) ? step['fields'] as DynamicFormField[] : []
        };
      }).filter((step) => step.formID > 0)
    };
  }

  async createRequest(payload: Record<string, unknown>): Promise<ServiceRequestResult> {
    const response = await firstValueFrom(this.api.post<unknown>('serviceMarketplacePublicRequest', payload));
    const data = this.payload(response, 'Talebin oluşturulamadı.');
    return {
      requestID: Number(data['requestID'] ?? 0),
      requestToken: String(data['requestToken'] ?? ''),
      submissionID: Number(data['submissionID'] ?? 0) || null,
      requestMode: String(data['requestMode'] ?? 'quote') === 'instant' ? 'instant' : 'quote',
      status: String(data['status'] ?? 'matching'),
      message: String(data['message'] ?? ''),
      matches: (Array.isArray(data['matches']) ? data['matches'] : []).map((raw) => {
        const match = raw as Record<string, unknown>;
        return {
          offerID: Number(match['offerID'] ?? 0),
          sellerID: Number(match['sellerID'] ?? 0),
          sellerName: String(match['sellerName'] ?? 'Hizmet veren'),
          price: match['price'] === null || match['price'] === undefined || match['price'] === '' ? null : Number(match['price']),
          currency: String(match['currency'] ?? ''),
          durationMinutes: Number(match['durationMinutes'] ?? 0),
          description: String(match['description'] ?? '')
        };
      }).filter((match) => match.offerID > 0)
    };
  }

  async requestStatus(requestID: number, requestToken: string): Promise<ServiceRequestStatus> {
    const response = await firstValueFrom(this.api.post<unknown>('serviceMarketplaceRequestStatus', { requestID, requestToken }));
    const data = this.payload(response, 'Talep durumu alınamadı.');
    const request = data['request'] && typeof data['request'] === 'object' ? data['request'] as Record<string, unknown> : {};
    const normalize = (raw: unknown, quote: boolean): ServiceRequestChoice => {
      const item = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
      return {
        quoteID: quote ? Number(item['quoteID'] ?? 0) || undefined : undefined,
        offerID: Number(item['offerID'] ?? 0),
        sellerID: Number(item['sellerID'] ?? 0),
        sellerName: String(item['sellerName'] ?? 'Hizmet veren'),
        amount: Number(item['amount'] ?? 0),
        currency: String(item['currency'] ?? 'TRY'),
        durationMinutes: Number(item['durationMinutes'] ?? 0),
        message: String(item[quote ? 'message' : 'description'] ?? ''),
        status: String(item['status'] ?? '')
      };
    };
    return {
      requestID: Number(request['requestID'] ?? requestID),
      requestMode: String(request['requestMode'] ?? 'quote') === 'instant' ? 'instant' : 'quote',
      status: String(request['status'] ?? 'matching'),
      serviceTitle: String(request['serviceTitle'] ?? 'Hizmet'),
      quotes: (Array.isArray(data['quotes']) ? data['quotes'] : []).map((item) => normalize(item, true)).filter((item) => !!item.quoteID),
      prices: (Array.isArray(data['prices']) ? data['prices'] : []).map((item) => normalize(item, false)).filter((item) => item.offerID > 0)
    };
  }

  async acceptSelection(requestID: number, requestToken: string, selection: { quoteID?: number; offerID?: number }): Promise<void> {
    const response = await firstValueFrom(this.api.post<unknown>('serviceMarketplaceSelectionAccept', { requestID, requestToken, ...selection }));
    this.payload(response, 'Hizmet veren seçilemedi.');
  }

  errorMessage(error: unknown, fallback: string): string {
    const source = error as { error?: unknown; message?: string };
    const queue: unknown[] = [source?.error, error];
    const visited = new Set<unknown>();
    while (queue.length) {
      const current = queue.shift();
      if (!current || typeof current !== 'object' || visited.has(current)) continue;
      visited.add(current);
      const record = current as Record<string, unknown>;
      if (typeof record['message'] === 'string' && record['message'].trim()) return record['message'];
      for (const key of ['data', 'error', 'response', 'apiData']) queue.push(record[key]);
    }
    return source?.message || fallback;
  }

  private normalizeItem(raw: Record<string, unknown>): ServiceCatalogItem {
    return {
      id: Number(raw['id'] ?? 0),
      versionID: Number(raw['versionID'] ?? 0),
      slug: String(raw['slug'] ?? raw['id'] ?? ''),
      title: String(raw['title'] ?? 'Hizmet'),
      description: String(raw['description'] ?? ''),
      imageUrl: this.media(raw['imageUrl']),
      imageSizes: raw['imageSizes'] && typeof raw['imageSizes'] === 'object' ? raw['imageSizes'] as Record<string, unknown> : {},
      flowType: this.flowType(raw['flowType']),
      providerCount: Number(raw['providerCount'] ?? 0),
      minPrice: raw['minPrice'] === null || raw['minPrice'] === undefined || raw['minPrice'] === '' ? null : Number(raw['minPrice']),
      currency: String(raw['currency'] ?? ''),
      durationMinutes: Number(raw['durationMinutes'] ?? 0),
      instantAvailable: this.boolean(raw['instantAvailable']),
      quoteAvailable: this.boolean(raw['quoteAvailable'])
    };
  }

  private catalogItems(payload: Record<string, unknown>): ServiceCatalogItem[] {
    const items = Array.isArray(payload['items']) ? payload['items'] : [];
    return items.map((item) => this.normalizeItem(item as Record<string, unknown>)).filter((item) => item.id > 0);
  }

  private payload(input: unknown, fallback: string): Record<string, unknown> {
    let current = input;
    for (let depth = 0; depth < 7; depth += 1) {
      if (!current || typeof current !== 'object' || Array.isArray(current)) throw new Error(fallback);
      const record = current as Record<string, unknown>;
      if (record['success'] === false || record['result'] === false) throw new Error(String(record['message'] || fallback));
      if ('items' in record || 'steps' in record || 'requestID' in record) return record;
      if (record['data'] && typeof record['data'] === 'object' && !Array.isArray(record['data'])) {
        current = record['data'];
        continue;
      }
      return record;
    }
    throw new Error(fallback);
  }

  private flowType(value: unknown): ServiceIntakeFlow['flowType'] {
    const type = String(value ?? 'none');
    return type === 'form' || type === 'stepper' ? type : 'none';
  }

  private boolean(value: unknown): boolean {
    return value === true || value === 1 || ['1', 'true', 'yes', 'on'].includes(String(value ?? '').toLowerCase());
  }

  private media(value: unknown): string | null {
    const raw = String(value ?? '').trim().replace(/\[size\]/gi, 'b');
    if (!raw || /^(javascript|data):/i.test(raw)) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    return `${this.site.config().apiBaseUrl.replace(/\/+$/, '')}/${raw.replace(/^\/+/, '')}`;
  }
}
